import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { ShopDto } from "@babackapp/private-api-client/types";
import { subMonths } from "date-fns";
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";

export type AnalyticsFilterName = "resolution" | "reason" | "country" | "sourceChannel";
export type AnalyticsFilterCondition = "in" | "notIn";
export type GroupBy = "productId" | "sku";
export type SortColumn = "ordersCount" | "returnsCount" | "returnRate";
export type SortDirection = "asc" | "desc";
export type ChartSmoothing = "instant" | "30d";

export type AnalyticsFilterValue = {
  condition: AnalyticsFilterCondition;
  value: string[];
};

type FilterMap = Map<AnalyticsFilterName, AnalyticsFilterValue>;

type AddFilterPayload = {
  name: AnalyticsFilterName;
  value: string;
  condition: AnalyticsFilterCondition;
  isMulti: boolean;
};

type RemoveFilterPayload = {
  name: AnalyticsFilterName;
  value: string;
};

type ClearFilterPayload = {
  name: AnalyticsFilterName;
};

type SetDateRangePayload = {
  from: number;
  to: number;
};

type SetGroupByPayload = {
  groupBy: GroupBy;
};

export type CountryData = {
  countryCode: string;
  country: string;
  ordersCount: number;
  returnsCount: number;
  returnRate: number;
};

type SetAvailableCountriesPayload = {
  countries: CountryData[];
};

type SetProductFilterPayload = {
  catalogId: string;
  productId: string;
  variantId?: string;
  title: string;
} | null;

type SetSortPayload = {
  column: SortColumn;
  direction: SortDirection;
};

enum ActionTypes {
  addFilter = "ADD_FILTER",
  removeFilter = "REMOVE_FILTER",
  clearFilter = "CLEAR_FILTER",
  clearAll = "CLEAR_ALL",
  setDateRange = "SET_DATE_RANGE",
  setGroupBy = "SET_GROUP_BY",
  setAvailableCountries = "SET_AVAILABLE_COUNTRIES",
  setProductFilter = "SET_PRODUCT_FILTER",
  setSort = "SET_SORT",
  setChartSmoothing = "SET_CHART_SMOOTHING",
  setIncludeAllOrders = "SET_INCLUDE_ALL_ORDERS",
  hydrateFromUrl = "HYDRATE_FROM_URL",
}

interface IAction<T, P> {
  type: T;
  payload: P;
}

type HydratePayload = {
  filters: FilterMap;
  dateRange: { from: number; to: number };
  groupBy: GroupBy;
  productFilter: { productId?: string; catalogId?: string; title: string } | null;
  sort: { column: SortColumn; direction: SortDirection };
  chartSmoothing: ChartSmoothing;
  includeAllOrders: boolean;
};

type Action =
  | IAction<ActionTypes.addFilter, AddFilterPayload>
  | IAction<ActionTypes.removeFilter, RemoveFilterPayload>
  | IAction<ActionTypes.clearFilter, ClearFilterPayload>
  | IAction<ActionTypes.clearAll, undefined>
  | IAction<ActionTypes.setDateRange, SetDateRangePayload>
  | IAction<ActionTypes.setGroupBy, SetGroupByPayload>
  | IAction<ActionTypes.setAvailableCountries, SetAvailableCountriesPayload>
  | IAction<ActionTypes.setProductFilter, SetProductFilterPayload>
  | IAction<ActionTypes.setSort, SetSortPayload>
  | IAction<ActionTypes.setChartSmoothing, ChartSmoothing>
  | IAction<ActionTypes.setIncludeAllOrders, boolean>
  | IAction<ActionTypes.hydrateFromUrl, HydratePayload>;

type AnalyticsFiltersContextValue = {
  filters: FilterMap;
  dateRange: { from: number; to: number };
  groupBy: GroupBy;
  sort: { column: SortColumn; direction: SortDirection };
  chartSmoothing: ChartSmoothing;
  includeAllOrders: boolean;
  shop: ShopDto;
  availableCountries: CountryData[];
  productFilter: { productId?: string; catalogId?: string; title: string } | null;
  isDataLoading: boolean;
  addFilter: (payload: AddFilterPayload) => void;
  removeFilter: (payload: RemoveFilterPayload) => void;
  clearFilter: (payload: ClearFilterPayload) => void;
  clearAll: () => void;
  setDateRange: (payload: SetDateRangePayload) => void;
  setGroupBy: (groupBy: GroupBy) => void;
  setSort: (column: SortColumn, direction: SortDirection) => void;
  setChartSmoothing: (smoothing: ChartSmoothing) => void;
  setIncludeAllOrders: (includeAllOrders: boolean) => void;
  setAvailableCountries: (
    countries: CountryData[],
  ) => void;
  setProductFilter: (
    filter: { productId?: string; catalogId?: string; title: string } | null,
  ) => void;
  setIsDataLoading: (isLoading: boolean) => void;
  isAnyFilterApplied: boolean;
};

interface State {
  filters: FilterMap;
  dateRange: { from: number; to: number };
  groupBy: GroupBy;
  sort: { column: SortColumn; direction: SortDirection };
  chartSmoothing: ChartSmoothing;
  includeAllOrders: boolean;
  availableCountries: CountryData[];
  productFilter: { productId?: string; catalogId?: string; title: string } | null;
}

export const AnalyticsFiltersContext = createContext<
  AnalyticsFiltersContextValue | undefined
>(undefined);

const FILTER_NAMES: AnalyticsFilterName[] = ["resolution", "reason", "country", "sourceChannel"];

function getDefaultDateRange() {
  return {
    from: subMonths(Date.now(), 1).getTime(),
    to: Date.now(),
  };
}

const DEFAULT_SORT: { column: SortColumn; direction: SortDirection } = {
  column: "returnRate",
  direction: "desc",
};

const initialState: State = {
  filters: new Map(),
  dateRange: getDefaultDateRange(),
  groupBy: "productId",
  sort: DEFAULT_SORT,
  chartSmoothing: "instant",
  includeAllOrders: false,
  availableCountries: [],
  productFilter: null,
};

/**
 * Parse URL search params into state
 */
function parseSearchParams(searchParams: URLSearchParams): Partial<HydratePayload> {
  const filters: FilterMap = new Map();

  // Parse filters (format: resolution=refund,exchange&country=FR,DE)
  for (const name of FILTER_NAMES) {
    const value = searchParams.get(name);
    if (value) {
      const values = value.split(",").filter(Boolean);
      if (values.length > 0) {
        filters.set(name, {
          condition: "in",
          value: values,
        });
      }
    }
  }

  // Parse date range
  const fromParam = searchParams.get("from");
  const toParam = searchParams.get("to");
  const defaultRange = getDefaultDateRange();
  const dateRange = {
    from: fromParam ? parseInt(fromParam, 10) : defaultRange.from,
    to: toParam ? parseInt(toParam, 10) : defaultRange.to,
  };

  // Validate date range
  if (isNaN(dateRange.from) || isNaN(dateRange.to)) {
    dateRange.from = defaultRange.from;
    dateRange.to = defaultRange.to;
  }

  // Parse groupBy
  const groupByParam = searchParams.get("groupBy");
  const groupBy: GroupBy =
    groupByParam === "sku" || groupByParam === "productId"
      ? groupByParam
      : "productId";

  // Parse product filter
  const catalogId = searchParams.get("catalogId");
  const productId = searchParams.get("productId");
  const productFilter = productId || catalogId
    ? { productId: productId || undefined, catalogId: catalogId || undefined, title: "" }
    : null;

  // Parse sort
  const sortColumnParam = searchParams.get("sortBy");
  const sortDirParam = searchParams.get("sortDir");
  const sortColumn: SortColumn =
    sortColumnParam === "ordersCount" || sortColumnParam === "returnsCount" || sortColumnParam === "returnRate"
      ? sortColumnParam
      : "returnRate";
  const sortDirection: SortDirection =
    sortDirParam === "asc" || sortDirParam === "desc" ? sortDirParam : "desc";

  // Parse chart smoothing
  const smoothingParam = searchParams.get("smoothing");
  const chartSmoothing: ChartSmoothing =
    smoothingParam === "instant" || smoothingParam === "30d" ? smoothingParam : "instant";

  // Parse includeAllOrders
  const includeAllOrders = searchParams.get("includeAll") === "1";

  return {
    filters,
    dateRange,
    groupBy,
    productFilter,
    sort: { column: sortColumn, direction: sortDirection },
    chartSmoothing,
    includeAllOrders,
  };
}

/**
 * Serialize state to URL search params
 */
function serializeToSearchParams(state: State): URLSearchParams {
  const params = new URLSearchParams();

  // Serialize filters
  for (const [name, filter] of state.filters) {
    if (filter.value.length > 0) {
      params.set(name, filter.value.join(","));
    }
  }

  // Serialize date range
  params.set("from", state.dateRange.from.toString());
  params.set("to", state.dateRange.to.toString());

  // Serialize groupBy (only if not default)
  if (state.groupBy !== "productId") {
    params.set("groupBy", state.groupBy);
  }

  // Serialize product filter
  if (state.productFilter) {
    if (state.productFilter.productId) {
      params.set("productId", state.productFilter.productId);
    }
    if (state.productFilter.catalogId) {
      params.set("catalogId", state.productFilter.catalogId);
    }
  }

  // Serialize sort (only if not default)
  if (state.sort.column !== "returnRate" || state.sort.direction !== "desc") {
    params.set("sortBy", state.sort.column);
    params.set("sortDir", state.sort.direction);
  }

  // Serialize chart smoothing (only if not default)
  if (state.chartSmoothing !== "instant") {
    params.set("smoothing", state.chartSmoothing);
  }

  // Serialize includeAllOrders (only if true)
  if (state.includeAllOrders) {
    params.set("includeAll", "1");
  }

  return params;
}

function filtersReducer(state: State, action: Action): State {
  switch (action.type) {
    case ActionTypes.hydrateFromUrl: {
      return {
        ...state,
        filters: action.payload.filters,
        dateRange: action.payload.dateRange,
        groupBy: action.payload.groupBy,
        productFilter: action.payload.productFilter,
        sort: action.payload.sort,
        chartSmoothing: action.payload.chartSmoothing,
        includeAllOrders: action.payload.includeAllOrders,
      };
    }
    case ActionTypes.addFilter: {
      const filters = new Map(state.filters);
      const filter = filters.get(action.payload.name);

      if (filter) {
        filters.set(action.payload.name, {
          ...filter,
          value: action.payload.isMulti
            ? [...filter.value, action.payload.value]
            : [action.payload.value],
          condition: action.payload.condition,
        });
      } else {
        filters.set(action.payload.name, {
          value: [action.payload.value],
          condition: action.payload.condition,
        });
      }

      return {
        ...state,
        filters,
      };
    }
    case ActionTypes.removeFilter: {
      const filters = new Map(state.filters);
      const filter = filters.get(action.payload.name);

      if (filter) {
        const value = filter.value.filter((v) => v !== action.payload.value);

        if (value.length) {
          filters.set(action.payload.name, {
            ...filter,
            value,
          });
        } else {
          filters.delete(action.payload.name);
        }
      }
      return {
        ...state,
        filters,
      };
    }
    case ActionTypes.clearFilter: {
      const filters = new Map(state.filters);
      filters.delete(action.payload.name);

      return {
        ...state,
        filters,
      };
    }
    case ActionTypes.clearAll:
      return {
        ...state,
        filters: new Map(),
        productFilter: null,
        includeAllOrders: false,
      };
    case ActionTypes.setDateRange:
      return {
        ...state,
        dateRange: action.payload,
      };
    case ActionTypes.setGroupBy:
      return {
        ...state,
        groupBy: action.payload.groupBy,
      };
    case ActionTypes.setAvailableCountries:
      return {
        ...state,
        availableCountries: action.payload.countries,
      };
    case ActionTypes.setProductFilter:
      return {
        ...state,
        productFilter: action.payload,
      };
    case ActionTypes.setSort:
      return {
        ...state,
        sort: {
          column: action.payload.column,
          direction: action.payload.direction,
        },
      };
    case ActionTypes.setChartSmoothing:
      return {
        ...state,
        chartSmoothing: action.payload,
      };
    case ActionTypes.setIncludeAllOrders:
      return {
        ...state,
        includeAllOrders: action.payload,
      };
    default:
      return state;
  }
}

export const AnalyticsFiltersProvider = ({ children }: PropsWithChildren) => {
  const { data: shop } = useCurrentShopSuspense();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state directly from URL params to avoid flash of default values
  const [state, dispatch] = useReducer(filtersReducer, searchParams, (params) => {
    const parsed = parseSearchParams(params);
    return {
      filters: parsed.filters ?? new Map(),
      dateRange: parsed.dateRange ?? getDefaultDateRange(),
      groupBy: parsed.groupBy ?? "productId",
      sort: parsed.sort ?? DEFAULT_SORT,
      chartSmoothing: parsed.chartSmoothing ?? "instant",
      includeAllOrders: parsed.includeAllOrders ?? false,
      availableCountries: [],
      productFilter: parsed.productFilter ?? null,
    };
  });
  const [isDataLoading, setIsDataLoading] = useState(false);

  // Track previous shop ID to detect shop changes
  const prevShopIdRef = useRef<string | null>(shop.id);

  // Reset filters when shop changes
  useEffect(() => {
    if (prevShopIdRef.current !== null && prevShopIdRef.current !== shop.id) {
      // Shop changed - reset all filters
      dispatch({ type: ActionTypes.clearAll, payload: undefined });
      // Clear URL params except date range
      const newParams = new URLSearchParams();
      newParams.set("from", state.dateRange.from.toString());
      newParams.set("to", state.dateRange.to.toString());
      setSearchParams(newParams, { replace: true });
    }
    prevShopIdRef.current = shop.id;
  }, [shop.id, setSearchParams, state.dateRange]);

  // Sync state to URL when it changes
  useEffect(() => {
    const newParams = serializeToSearchParams(state);
    setSearchParams(newParams, { replace: true });
  }, [
    state.filters,
    state.dateRange,
    state.groupBy,
    state.productFilter,
    state.sort,
    state.chartSmoothing,
    state.includeAllOrders,
    setSearchParams,
  ]);

  const clearAll = useCallback(() => {
    dispatch({ type: ActionTypes.clearAll, payload: undefined });
  }, []);

  const addFilter = useCallback((payload: AddFilterPayload) => {
    dispatch({ type: ActionTypes.addFilter, payload });
  }, []);

  const removeFilter = useCallback((payload: RemoveFilterPayload) => {
    dispatch({ type: ActionTypes.removeFilter, payload });
  }, []);

  const clearFilter = useCallback((payload: ClearFilterPayload) => {
    dispatch({ type: ActionTypes.clearFilter, payload });
  }, []);

  const setDateRange = useCallback((payload: SetDateRangePayload) => {
    dispatch({ type: ActionTypes.setDateRange, payload });
  }, []);

  const setGroupBy = useCallback((groupBy: GroupBy) => {
    dispatch({ type: ActionTypes.setGroupBy, payload: { groupBy } });
  }, []);

  const setSort = useCallback((column: SortColumn, direction: SortDirection) => {
    dispatch({ type: ActionTypes.setSort, payload: { column, direction } });
  }, []);

  const setChartSmoothing = useCallback((smoothing: ChartSmoothing) => {
    dispatch({ type: ActionTypes.setChartSmoothing, payload: smoothing });
  }, []);

  const setIncludeAllOrders = useCallback((includeAllOrders: boolean) => {
    dispatch({ type: ActionTypes.setIncludeAllOrders, payload: includeAllOrders });
  }, []);

  const setAvailableCountries = useCallback(
    (countries: CountryData[]) => {
      dispatch({
        type: ActionTypes.setAvailableCountries,
        payload: { countries },
      });
    },
    [],
  );

  const setProductFilter = useCallback(
    (filter: { productId?: string; catalogId?: string; title: string } | null) => {
      dispatch({
        type: ActionTypes.setProductFilter,
        payload: filter,
      });
    },
    [],
  );

  const isAnyFilterApplied =
    state.filters.size > 0 || state.productFilter !== null || state.includeAllOrders;

  return (
    <AnalyticsFiltersContext.Provider
      value={{
        filters: state.filters,
        dateRange: state.dateRange,
        groupBy: state.groupBy,
        sort: state.sort,
        chartSmoothing: state.chartSmoothing,
        includeAllOrders: state.includeAllOrders,
        shop,
        availableCountries: state.availableCountries,
        productFilter: state.productFilter,
        isDataLoading,
        addFilter,
        removeFilter,
        clearFilter,
        clearAll,
        setDateRange,
        setGroupBy,
        setSort,
        setChartSmoothing,
        setIncludeAllOrders,
        setAvailableCountries,
        setProductFilter,
        setIsDataLoading,
        isAnyFilterApplied,
      }}
    >
      {children}
    </AnalyticsFiltersContext.Provider>
  );
};
